import { useState } from "react";
import { useTranslation, untranslated } from "@i18n";
import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import ModalStateDialogComponent from "@CubeComponents/html/modal/ModalWithState";
import Text from "@CubeComponents/html/Text/Text";
import { useAppDispatch as useDispatch } from "@Reducers/hooks";
import { showToastNotification } from "@Reducers/toastSlice";
import { TOAST_TYPE_ERROR, TOAST_TYPE_SUCCESS } from "@Services/constants";
import { revokeExternalLink, type ExternalLinkListItem } from "./externalLinks.api";

/**
 * Confirmation before revoking one link.
 *
 * Deliberately per-link: there is no bulk revoke in v1, and the warning spells out that the effect
 * is immediate — a recipient with the page already open loses access on their next action, not at
 * the end of some session.
 */
type RevokeExternalLinkModalProps = {
  link: ExternalLinkListItem;
  onClose: () => void;
  onRevoked: () => void;
};

function RevokeExternalLinkModal({ link, onClose, onRevoked }: Readonly<RevokeExternalLinkModalProps>) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [busy, setBusy] = useState(false);

  const handleConfirm = async () => {
    setBusy(true);
    try {
      const res = await revokeExternalLink(link._id);
      if (!res.status) {
        dispatch(showToastNotification({ content: untranslated(res.errorMessage), type: TOAST_TYPE_ERROR }));
        return;
      }
      dispatch(showToastNotification({ content: t("Link revoked"), type: TOAST_TYPE_SUCCESS }));
      onRevoked();
    } finally {
      setBusy(false);
    }
  };

  return (
    <ModalStateDialogComponent
      isVisible
      width="27.5rem"
      headerTitle={t("Revoke link")}
      onCancel={onClose}
      modelContent={
        <div className="flex flex-col gap-3 py-2">
          <Text className="text-sm">{t("Are you sure you want to revoke this link?")}</Text>
          <div className="rounded-md border border-warning bg-bg_warning px-3 py-2">
            <Text noWrap className="text-xs leading-relaxed">
              {t(
                '"{{name}}" will stop working immediately and recipients will see an invalid-link page. Access history is retained for audit.',
                { name: link.name },
              )}
            </Text>
          </div>
        </div>
      }
      footerContent={
        <div className="flex gap-3">
          <ButtonComponent buttonType={BUTTON_TYPES.GRADIENT_BUTTON} disabled={busy} onClick={handleConfirm}>
            {t("Confirm")}
          </ButtonComponent>
          <ButtonComponent buttonType={BUTTON_TYPES.BORDER_BUTTON} onClick={onClose}>
            {t("Cancel")}
          </ButtonComponent>
        </div>
      }
    />
  );
}

export default RevokeExternalLinkModal;
